import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import ModuleHeader from "../components/ModuleHeader";
import DateDropdown from "@/components/DateDropdown";
import { rem2px, REM_CONFIG } from "@/utils/fontSize";
import moment from "moment";
// ==========================
// 核心组件
// ==========================
const WarningReminders = ({ className = "", initDate = moment().format("YYYY-MM"), }) => {
    const ROW_HEIGHT = rem2px(REM_CONFIG.rowHeight);
    const [selectedDate, setSelectedDate] = useState(initDate);
    const [tableHeight, setTableHeight] = useState("auto");
    const [isScrolling, setIsScrolling] = useState(true);
    const tableContainerRef = useRef(null);
    const scrollTimer = useRef(null);
    // 表格数据
    const [tableData, setTableData] = useState([
        {
            projectName: "煤炭直接液化国家工程研究中心科研条件保障能力建设(2023)",
            projectType: "集团项目",
            projectAmount: 625,
            procurement: 437.73,
        },
        {
            projectName: "煤炭直接液化国家工程研究中心条件保障能力建设（2024）",
            projectType: "国家项目",
            projectAmount: 1814,
            procurement: 1215,
        },
        {
            projectName: "煤炭直接液化国家工程研究中心科研条件保障能力建设（2025）",
            projectType: "2025新立集团项目",
            projectAmount: 1492,
            procurement: 1074.66,
        },
        {
            projectName: "大规模新型煤直接液化技术",
            projectType: "国家项目",
            projectAmount: 2854,
            procurement: 1969.6,
        },
        {
            projectName: "煤炭直接定向调控制备高端精细化学品技术",
            projectType: "国家项目",
            projectAmount: 2656,
            procurement: 2019,
        },
        {
            projectName: "1025项目",
            projectType: "集团十大科技攻关项目",
            projectAmount: 1320,
            procurement: 924,
        },
        {
            projectName: "二代煤直接液化工艺开发",
            projectType: "集团项目",
            projectAmount: 1711,
            procurement: 1369,
        },
        {
            projectName: "煤直接液化铁催化剂回收及新型催化剂制备工艺研究",
            projectType: "国家项目",
            projectAmount: 541,
            procurement: 433,
        },
        {
            projectName: "富油煤直接液化工程化技术研究",
            projectType: "国家项目",
            projectAmount: 1482,
            procurement: 963.74,
        },
        {
            projectName: "煤直接液化油沸腾床加氢稳定催化剂研究开发",
            projectType: "国家项目",
            projectAmount: 1425,
            procurement: 1140.2,
        },
        {
            projectName: "沸腾床-固定床组合溶剂加氢工艺研究",
            projectType: "化工公司项目",
            projectAmount: 287,
            procurement: 230,
        },
        {
            projectName: "煤直接液化新型反应器优化和过程强化研究",
            projectType: "化工公司项目",
            projectAmount: 640,
            procurement: 448,
        },
    ]);
    // 计算表格高度
    useEffect(() => {
        const calculateTableHeight = () => {
            if (!tableContainerRef.current)
                return;
            const container = tableContainerRef.current;
            const parent = container.parentElement;
            if (!parent)
                return;
            const header = container.previousElementSibling;
            const headerHeight = header?.offsetHeight || 0;
            const unitText = container.querySelector("p");
            const unitTextHeight = unitText ? unitText.offsetHeight : 0;
            const topSpacing = rem2px(REM_CONFIG.spacing.top);
            const totalTopHeight = headerHeight + unitTextHeight + topSpacing;
            const parentHeight = parent.offsetHeight;
            const availableHeight = parentHeight - totalTopHeight;
            const contentHeight = tableData.length * ROW_HEIGHT;
            if (contentHeight <= availableHeight || availableHeight < rem2px(0.1)) {
                setTableHeight("auto");
            }
            else {
                setTableHeight(Math.floor(availableHeight));
            }
        };
        calculateTableHeight();
        const resizeObserver = new ResizeObserver(calculateTableHeight);
        requestAnimationFrame(() => {
            if (tableContainerRef.current)
                resizeObserver.observe(tableContainerRef.current);
        });
        return () => resizeObserver.disconnect();
    }, [tableData, ROW_HEIGHT]);
    // 自动滚动
    useEffect(() => {
        if (scrollTimer.current)
            clearInterval(scrollTimer.current);
        if (tableHeight === "auto" || !isScrolling || tableData.length === 0)
            return;
        const scrollContainer = tableContainerRef.current?.querySelector(".table-body");
        if (!scrollContainer)
            return;
        scrollTimer.current = setInterval(() => {
            const { scrollTop, clientHeight, scrollHeight } = scrollContainer;
            const scrollStep = REM_CONFIG.spacing.scrollStep;
            let newScrollTop = scrollTop + scrollStep;
            if (newScrollTop + clientHeight >= scrollHeight - 1)
                newScrollTop = 0;
            scrollContainer.scrollTop = newScrollTop;
        }, 50);
        return () => {
            if (scrollTimer.current)
                clearInterval(scrollTimer.current);
        };
    }, [tableHeight, isScrolling, tableData.length]);
    // 鼠标悬停暂停
    const handleMouseEnter = () => setIsScrolling(false);
    const handleMouseLeave = () => setIsScrolling(true);
    // 日期切换
    const fetchChartData = async (date) => {
        setSelectedDate(date);
    };
    return (_jsxs("div", { className: `relative bg-gradient-to-t from-[rgba(7,31,75,0.5)] to-[rgba(6,32,66,0.2)] flex flex-col ${className} min-h-0`, children: [_jsx(ModuleHeader, { title: "\u9879\u76EE\u9884\u7B97\u91C7\u8D2D\u5BF9\u6BD4", children: _jsx(DateDropdown, { initDate: initDate, onDateChange: fetchChartData }) }), _jsxs("div", { ref: tableContainerRef, className: "flex-1 w-full min-h-0", style: {
                    padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                    overflow: "hidden",
                }, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsx("p", { style: {
                            margin: `${REM_CONFIG.spacing.mt}rem 0 ${REM_CONFIG.spacing.mb}rem`,
                            color: "#FFF",
                            fontSize: REM_CONFIG.fontSize.small,
                        }, children: "\u5355\u4F4D\uFF1A\u4E07\u5143" }), _jsxs("div", { className: "table-wrapper", style: { width: "100%", overflow: "hidden" }, children: [_jsx("table", { style: {
                                    width: "100%",
                                    tableLayout: "fixed",
                                    borderCollapse: "collapse",
                                }, children: _jsx("thead", { children: _jsxs("tr", { style: { backgroundColor: "#062a58", color: "#0ff" }, children: [_jsx("th", { style: {
                                                    width: "35%",
                                                    padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                                                    height: ROW_HEIGHT + "px",
                                                    fontSize: REM_CONFIG.fontSize.small,
                                                    textAlign: "left",
                                                }, children: "\u9879\u76EE" }), _jsx("th", { style: {
                                                    width: "25%",
                                                    padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                                                    height: ROW_HEIGHT + "px",
                                                    fontSize: REM_CONFIG.fontSize.small,
                                                    textAlign: "left",
                                                }, children: "\u7C7B\u522B" }), _jsx("th", { style: {
                                                    width: "20%",
                                                    padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                                                    height: ROW_HEIGHT + "px",
                                                    fontSize: REM_CONFIG.fontSize.small,
                                                    textAlign: "left",
                                                }, children: "\u9884\u7B97" }), _jsx("th", { style: {
                                                    width: "20%",
                                                    padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                                                    height: ROW_HEIGHT + "px",
                                                    fontSize: REM_CONFIG.fontSize.small,
                                                    textAlign: "left",
                                                }, children: "\u91C7\u8D2D" })] }) }) }), _jsx("div", { className: "table-body", style: {
                                    height: tableHeight === "auto" ? "auto" : tableHeight + "px",
                                    overflowY: "auto",
                                    scrollbarWidth: "none",
                                    msOverflowStyle: "none",
                                }, children: _jsx("table", { style: {
                                        width: "100%",
                                        tableLayout: "fixed",
                                        borderCollapse: "collapse",
                                    }, children: _jsx("tbody", { children: tableData.map((item, idx) => (_jsxs("tr", { style: {
                                                backgroundColor: idx % 2 === 0 ? "transparent" : "#062654",
                                                height: ROW_HEIGHT + "px",
                                                cursor: "pointer",
                                            }, onMouseEnter: (e) => (e.currentTarget.style.backgroundColor = "#1A446E"), onMouseLeave: (e) => (e.currentTarget.style.backgroundColor =
                                                idx % 2 === 0 ? "transparent" : "#062654"), children: [_jsx("td", { style: {
                                                        padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                                                        color: "#FFF",
                                                        fontSize: REM_CONFIG.fontSize.small,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }, children: item.projectName }), _jsx("td", { style: {
                                                        padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                                                        color: "#FFF",
                                                        fontSize: REM_CONFIG.fontSize.small,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }, children: item.projectType }), _jsx("td", { style: {
                                                        padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                                                        color: "#FFF",
                                                        fontSize: REM_CONFIG.fontSize.small,
                                                    }, children: item.projectAmount }), _jsx("td", { style: {
                                                        padding: `0 ${REM_CONFIG.spacing.paddingX}rem`,
                                                        color: "#FFF",
                                                        fontSize: REM_CONFIG.fontSize.small,
                                                    }, children: item.procurement })] }, idx))) }) }) })] }), _jsx("style", { children: `
            .table-body::-webkit-scrollbar { display: none; }
            th, td { border: none; outline: none; }
          ` })] })] }));
};
export default WarningReminders;
